import RichPlaceholderCompiler from '../compilers/RichPlaceholderCompiler'
import { WIDGET_PREVIEW_SIZE } from '../constants'
import IdService from 'id-service'

function parseElementStyle(props) {
  const { widget, isHyperlinkEnabled, zModifier, isRenderingInsideLayerTool, isRenderingInsideAceEditor } = props
  let widgetLayout = widget.layout || {}
  let widgetBaseStyle = widget.baseStyle ? JSON.parse(JSON.stringify(widget.baseStyle)) : {}
  let animationTypeMap = {}
  // In preparation for multiple animations, animation fill mode is converted to 'forwards' instead of 'both'.
  // This is because the 'both' fill mode does not work with multiple animations. This then requires us to set
  // the position of the actual widget to the starting point of the animation otherwise the widget will become visible
  // in the end position of the animation and not the starting position.
  if (widget.animations?.length && !isRenderingInsideAceEditor) {
    widget.animations.forEach(a => {
      if (
        (a.type !== 'transition' && animationTypeMap[a.type]) ||
        (a.type === 'transition' && animationTypeMap[a.targetStyle.name])
      ) {
        return
      }

      switch (true) {
        case a.type === 'move' || a.type === 'resize':
          animationTypeMap['move'] = true
          animationTypeMap['resize'] = true
          widgetLayout = {
            ...widgetLayout,
            x: a.animationStates[0].layout.x,
            y: a.animationStates[0].layout.y,
            width: a.animationStates[0].layout.width,
            height: a.animationStates[0].layout.height,
          }
          break
        case a.type === 'transition':
          animationTypeMap[a.targetStyle.name] = true
          widgetBaseStyle[a.targetStyle.name] = a.animationStates[0].transition.value
          break
        case a.type === 'rotation':
          animationTypeMap[a.type] = true
          widgetLayout = {
            ...widgetLayout,
            zRotation: a.animationStates[0].rotation.value,
          }
          widgetBaseStyle.horizontal = a.animationStates[0].horizontal.value
          widgetBaseStyle.vertical = a.animationStates[0].vertical.value
          break
      }
    })
  }
  // if inside ace editor, set to current base style, not starting point of animation
  if (isRenderingInsideAceEditor) {
    widgetBaseStyle = widget.baseStyle
  }
  const scale =
    isRenderingInsideLayerTool && WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height) < 1
      ? `scale(${WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height)})`
      : ''

  return {
    position: 'absolute',
    overflow: 'hidden',
    // hide widget if visibility off and widget is inside template (does not hide widget from actual ad)
    // strict false check is necessary as isLayerVisible undefined should default to true (DO NOT CHANGE to falsy !widget.isLayerVisible check)
    // widget visibility by default should be true
    display:
      widget.isLayerVisible === false && isRenderingInsideAceEditor && !isRenderingInsideLayerTool ? 'none' : 'grid',
    'place-items': 'center',

    // Allow link-less widgets to be non-interactive, so that user could
    // interact with underlying links
    'pointer-events': isHyperlinkEnabled && !widget.href && 'none',

    width: `${widgetLayout.width}px`,
    height: `${widgetLayout.height}px`,
    top: isRenderingInsideLayerTool ? '' : `${widgetLayout.y}px`,
    left: isRenderingInsideLayerTool ? '' : `${widgetLayout.x}px`,
    'z-index': widgetLayout.z + zModifier,

    background: widgetBaseStyle.background || 'transparent',
    // 0 values do not get rendered so we use percent for 0 values
    opacity: widgetBaseStyle.opacity === 0 ? '0%' : widgetBaseStyle.opacity || 1,

    'border-width': `${widgetBaseStyle['border-width'] || 0}px`,
    'border-radius': `${widgetBaseStyle['border-radius'] * 100 || 0}px`,
    'border-style': widgetBaseStyle['border-style'],
    'border-color': widgetBaseStyle['border-color'],

    transform: isRenderingInsideLayerTool
      ? `${scale}`
      : `rotate(${widgetLayout.zRotation || 0}deg) rotateY(${widgetBaseStyle.horizontal ||
          0}deg) rotateX(${widgetBaseStyle.vertical || 0}deg)`,
  }
}

function withSVGContainer(rawSVGCode, $h) {
  //context was bound to this
  const props = this.props
  //account for any native events the template editor is trying to apply (ie mouseover, contextmenu)
  const nativeOn = (this.data && this.data.nativeOn) || {}
  let svgStyle = JSON.parse(JSON.stringify(props.widget.svgStyle))
  let animationTypeMap = {}

  if (props.widget.animations?.length && !props.isRenderingInsideAceEditor) {
    props.widget.animations.forEach(a => {
      if (
        !(
          a.type === 'transition' &&
          !animationTypeMap[a.targetStyle.name] &&
          svgStyle[0][a.targetStyle.name === 'stroke-width' ? 'strokeWidth' : a.targetStyle.name]
        )
      ) {
        return
      }
      animationTypeMap[a.targetStyle.name] = true
      // animation stroke-width in targetStyle.name is strokeWidth in svgStyle (this is the only property that does not match)
      svgStyle[0][a.targetStyle.name === 'stroke-width' ? 'strokeWidth' : a.targetStyle.name] =
        a.animationStates[0].transition.value
    })
  }

  // if inside ace editor, set to current base style, not starting point of animation
  if (props.isRenderingInsideAceEditor) {
    svgStyle = props.widget.svgStyle
  }

  // SVG widgets have a svgStyle (fill, stroke, stroke-width) key in addition to the baseStyle (background and border style)
  // The svgStyle key is an array which can hold the styles of each individual element in the SVG
  // Current setup only allows single element SVGs to be styled, but eventually we want to be able to allow each individual
  // element in an SVG to be manipulable, thus the array structure
  let finalSVGCode
  if (svgStyle.length === 1) {
    // Replace SVG string-placeholders with actual values, ie Fill, Stroke, etc
    finalSVGCode = rawSVGCode
      .replace(/fill="(.*?)\"/, `class="${props.svgAnimationClass}" fill="${svgStyle[0].fill || 'black'}"`)
      .replace(/stroke="(.*?)\"/, `stroke="${svgStyle[0].stroke || 'black'}"`)
      .replace(
        /stroke-width="(.*?)\"/,
        // make sure 0 value is also stored as 0, otherwise falsy value will set it to 1
        `
          stroke-width="${svgStyle[0].strokeWidth === 0 ? svgStyle[0].strokeWidth : svgStyle[0].strokeWidth || '1'}"
          vector-effect="non-scaling-stroke"
        `
      )
  } else {
    finalSVGCode = rawSVGCode
  }

  // Some SVGs (eg. gradient styled) are styled using fill url which follows a pattern id
  // (eg. fill="url(#idname)" or fill:url(#idname)). CSR replaces all ids to prevent non-unique
  // ids but this disconnects the svg styling due to the SVG fill url pattern id not being
  // replaced at the same time. We therefore create our own unique ids here which CSR can identify
  // and replace both id and fill url pattern id with new unique ids, keeping the SVG's connection
  // to its style as well as uniqifying the id to the specific template.

  // There are two ways to use fill url with pattern id (fill="url(#...) OR fill:url(#...))
  const urlRegex = new RegExp(/fill(="|:)url\(.*?\)/gi)
  let matches
  let newMatchId
  let currentMatchId
  let matchRegex
  let replacementValuesMap

  // FIXME: regex matching is doing > 1 pass, each with a different regex. First regex does the discovery
  // and the second does the replacement - these can be combined into one operation, and a single regex expression
  // can be made to pattern match all variations.  Second regex is also in a loop so essentially we are doing at
  // least 4 passes in total over the string. Need to revisit and switch to single pass regex match and replace.
  while ((matches = urlRegex.exec(finalSVGCode))) {
    replacementValuesMap = {}
    newMatchId = IdService.generate('adacadoSVG') // generate unique id for this fill pattern
    currentMatchId = matches[0].substring(matches[0].indexOf('#') + 1, matches[0].indexOf(')')) // extract current id of SVG to replace

    // regex to match and replace id and fill url pattern id
    matchRegex = new RegExp(`(fill(="|:)url\\(\\#${currentMatchId}\\))|(id="${currentMatchId}")`, 'g')

    // create replacement values map to replace values accordingly in svg string
    // set key names for current values of id and fill url pattern ids
    // set current values in map as keys with new values as the value
    replacementValuesMap[`id="${currentMatchId}"`] = `id="${newMatchId}"`
    replacementValuesMap[`fill="url(#${currentMatchId})`] = `fill="url(#${newMatchId})`
    replacementValuesMap[`fill:url(#${currentMatchId})`] = `fill:url(#${newMatchId})`

    // replace the id and the fill url with our newly created ids
    // matched strings are replaced with the corresponding key's value
    finalSVGCode = finalSVGCode.replace(matchRegex, key => replacementValuesMap[key])
  }

  // inventoryIndex from prop contains correct inventory index information
  // apply to widget.rule before compiling
  let widgetRule = this.props.widget.rule
  if (widgetRule) {
    widgetRule.inventoryIndex = this.props.inventoryIndex
  }

  return $h(
    'div',
    {
      on: {
        ...nativeOn,
      },
      style: parseElementStyle(props),
      RHStyles: widgetRule ? escape(this.props.contentCompiler.compile(widgetRule)) : '',
      attrs: {
        'data-adacadotransitiontype': 'transition',
        'data-adacadopropname': props.widget?.value[0]?.content?.name
          ? `${props.widget.value[0].content.name}${props.widget.id}`
          : false,
        'widget-id': `widget${props.widget.id}`,
        class: props.animationClass,
      },
      domProps: {
        innerHTML: finalSVGCode,
      },
    },
    []
  )
}

function withAnchorContainer(sourceElement, $h) {
  // Wrap widget in an anchor tag if there is a value for href property
  if (this.isHyperlinkEnabled && this.widget.href) {
    let widgetHref = JSON.parse(JSON.stringify(this.widget.href))
    widgetHref.inventoryIndex = this.inventoryIndex
    return $h(
      'a',
      {
        attrs: {
          // href attributes of widget are completely controlled by the end-user,
          // so it is important to sanitize the inputs in all cases.
          // FIXME: we are performing bare minimum checks that replaces `<` and `>`
          // Due to lack of support from vue(see  https://github.com/vuejs/vue/issues/8657),
          // we would have to incorporate other libraries for a complete sanitization logic
          href: escapeLinkAttribute(this.contentCompiler.compile(widgetHref, { isURL: true })),
          target: '_blank',
        },
      },
      [sourceElement]
    )
  } else {
    // pointer-events attribute will be set to "none" when isHyperlinkEnabled is true but widget.href is not defined
    // this allows the widget to inherit link behavior from the background
    return sourceElement
  }
}

function escape(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

function escapeLinkAttribute(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

export default {
  functional: true,
  props: {
    widget: {
      type: Object,
      required: true,
    },
    animationClass: {
      type: String,
      default: '',
    },
    svgAnimationClass: {
      type: String,
      default: '',
    },
    templateId: Number,
    isRenderingInsideAceEditor: {
      type: Boolean,
      default: false,
    },
    isRenderingInsideLayerTool: {
      type: Boolean,
      default: false,
    },
    isHyperlinkEnabled: Boolean,
    zModifier: {
      type: Number,
      default: 0,
    },
    inventoryIndex: {
      type: Number,
      default: 0,
    },
    // CanvasTranspiler will pass this a prop where the RichPlaceholderCompiler's mode is set to 'placeholder'
    // which generates richplaceholders in the output html as opposed to just the default value (for viewing in Ace Editor)
    contentCompiler: {
      type: Object,
      default() {
        return new RichPlaceholderCompiler()
      },
    },
  },
  render($h, context) {
    const { widget } = context.props

    // decorate with div, and then finally anchor
    return withAnchorContainer.call(context.props, withSVGContainer.call(context, widget.value.content, $h), $h)
  },
}
